/* AgentMeeting — Settings (account / preferences / API / notifications) */

import React from 'react';
import { MarketingBar } from '../shared/marketing-bar';
import { auth, me, HTTPError } from '../shared/api';
import { useAuth } from '../shared/auth';
// settings.tsx defines its own local Toggle; shared one is unused here

// keep auth imported (even if unused directly) to make refactors smoother
void auth;

export function Settings({ goTo }) {
  const [section, setSection] = React.useState('account');
  const { signOut } = useAuth();
  const sections = [
    { id: 'account',       label: '账户',          icon: '◯' },
    { id: 'preferences',   label: '偏好',          icon: '◇' },
    { id: 'llm',           label: 'LLM & API key', icon: '⌬' },
    { id: 'notifications', label: '通知',          icon: '◓' },
    { id: 'workspace',     label: 'Workspace',     icon: '◫' },
    { id: 'security',      label: '安全 & 会话',   icon: '◈' },
    { id: 'data',          label: '数据 & 导出',   icon: '⎘' },
    { id: 'danger',        label: '危险区',        icon: '⚠' },
  ];

  const handleSignOut = () => {
    signOut();
    goTo('landing');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)', color: 'var(--fg-0)' }}>
      <MarketingBar here={null} goTo={goTo} signedIn={true} />

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: 'calc(100vh - 50px)' }}>
        {/* sidebar */}
        <aside style={{
          borderRight: '1px solid var(--line-1)',
          background: 'var(--bg-1)', padding: '24px 0',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '0 20px 16px' }}>
            <span className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.14em' }}>
              [SETTINGS]
            </span>
            <h2 style={{ fontSize: 18, fontWeight: 500, margin: '8px 0 0' }}>
              控制面板
            </h2>
          </div>
          <nav className="col" style={{ gap: 1 }}>
            {sections.map(s => {
              const on = section === s.id;
              return (
                <button key={s.id} onClick={() => setSection(s.id)} className="hov"
                  style={{
                    padding: '10px 20px', textAlign: 'left',
                    background: on ? 'var(--bg-3)' : 'transparent',
                    color: on ? 'var(--fg-0)' : 'var(--fg-1)',
                    border: 'none', borderLeft: '2px solid ' + (on ? 'var(--live)' : 'transparent'),
                    cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center',
                    fontSize: 13,
                  }}>
                  <span className="mono dim" style={{ width: 14 }}>{s.icon}</span>
                  {s.label}
                </button>
              );
            })}
          </nav>

          <div style={{
            margin: '24px 16px 0', padding: 12,
            border: '1px dashed var(--line-2)', borderRadius: 4,
            fontSize: 11, color: 'var(--fg-2)',
          }}>
            <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em', marginBottom: 4 }}>
              [WORKSPACE]
            </div>
            <div style={{ fontWeight: 500, color: 'var(--fg-0)' }}>李明的工作台</div>
            <div className="dim mono tiny" style={{ marginTop: 2 }}>Pro · 14d trial · 11 days left</div>
            <a className="link mono tiny" style={{ marginTop: 6, display: 'inline-block' }}
              onClick={() => goTo('billing')}>
              → manage billing
            </a>
          </div>

          <div style={{ marginTop: 'auto', padding: '16px' }}>
            <button className="btn ghost mono tiny" onClick={handleSignOut}
              style={{ width: '100%', color: 'var(--fg-1)' }}>
              $ sign out
            </button>
          </div>
        </aside>

        {/* content */}
        <main style={{ padding: '32px 48px', maxWidth: 880 }}>
          {section === 'account' && <SecAccount />}
          {section === 'preferences' && <SecPrefs />}
          {section === 'llm' && <SecLLM />}
          {section === 'notifications' && <SecNotifs />}
          {section === 'workspace' && <SecWorkspace />}
          {section === 'security' && <SecSecurity />}
          {section === 'data' && <SecData />}
          {section === 'danger' && <SecDanger goTo={goTo} handleSignOut={handleSignOut} />}
        </main>
      </div>
    </div>
  );
}

function SecHeader({ tag, title, sub }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <span className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.12em' }}>
        [{tag}]
      </span>
      <h1 style={{ fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', margin: '8px 0 4px' }}>
        {title}
      </h1>
      <p style={{ color: 'var(--fg-1)', fontSize: 13, margin: 0 }}>{sub}</p>
    </div>
  );
}

function Card({ title, sub, children, action }: any) {
  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)', marginBottom: 16,
    }}>
      {(title || sub) && (
        <div className="flex between center" style={{
          padding: '14px 18px', borderBottom: '1px solid var(--line-1)',
          background: 'var(--bg-0)',
        }}>
          <div>
            {title && <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>}
            {sub && <div className="meta" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
          </div>
          {action}
        </div>
      )}
      <div style={{ padding: 18 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, hint, children }: any) {
  return (
    <div className="flex between" style={{
      gap: 24, padding: '10px 0',
      borderBottom: '1px solid var(--line-1)', alignItems: 'center',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: 'var(--fg-0)' }}>{label}</div>
        {hint && <div className="meta" style={{ fontSize: 11, marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function Toggle({ on, onChange }: any) {
  return (
    <button onClick={() => onChange(!on)} style={{
      width: 36, height: 20, borderRadius: 10,
      border: '1px solid ' + (on ? 'var(--live)' : 'var(--line-3)'),
      background: on ? 'var(--live)' : 'var(--bg-3)',
      position: 'relative', cursor: 'pointer', padding: 0,
      transition: 'all 120ms',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 18 : 2,
        width: 14, height: 14, borderRadius: '50%',
        background: on ? 'var(--bg-0)' : 'var(--fg-2)',
        transition: 'left 120ms',
      }} />
    </button>
  );
}

function SoonTag() {
  return (
    <span className="tag mono tiny" style={{
      borderColor: 'var(--line-3)', color: 'var(--fg-2)', marginLeft: 8,
    }}>soon</span>
  );
}

function SoonBanner({ message = '此功能正在路上 · 后端 endpoint 还没上线' }: { message?: string }) {
  return (
    <div style={{
      padding: '10px 14px', marginBottom: 16,
      border: '1px dashed var(--line-3)', borderRadius: 4,
      background: 'var(--bg-2)', color: 'var(--fg-2)', fontSize: 12,
    }}>
      <span className="mono tiny" style={{ color: 'var(--fg-2)', marginRight: 6, letterSpacing: '0.1em' }}>
        [COMING SOON]
      </span>
      {message}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      padding: '10px 14px', marginBottom: 16,
      border: '1px solid var(--err)', borderRadius: 4,
      background: 'rgba(239,76,76,0.06)', color: 'var(--err)', fontSize: 12,
    }}>
      <span className="mono tiny" style={{ marginRight: 6, letterSpacing: '0.1em' }}>
        [ERROR]
      </span>
      {message}
    </div>
  );
}

function SecAccount() {
  const { user, refresh } = useAuth();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [tz, setTz] = React.useState('Asia/Shanghai (UTC+8)');
  const [saving, setSaving] = React.useState(false);
  const [comingSoon, setComingSoon] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedOk, setSavedOk] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const initials = React.useMemo(() => {
    const src = (user?.name || user?.email || '?').trim();
    if (!src) return '?';
    const ascii = src.replace(/[^A-Za-z0-9]/g, '');
    if (ascii.length >= 2) return ascii.slice(0, 2).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  }, [user]);

  const onSave = async () => {
    setError(null);
    setSavedOk(false);
    setSaving(true);
    try {
      await me.update({ name: name.trim() || undefined, email: email.trim() || undefined });
      await refresh();
      setSavedOk(true);
    } catch (err) {
      if (err instanceof HTTPError && err.status === 404) {
        setComingSoon(true);
      } else if (err instanceof HTTPError) {
        setError(`保存失败 (HTTP ${err.status})`);
      } else {
        setError('保存失败 · 请检查网络');
      }
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    setName(user?.name || '');
    setEmail(user?.email || '');
    setError(null);
    setSavedOk(false);
  };

  if (!user) {
    return (
      <>
        <SecHeader tag="ACCOUNT" title="个人资料"
          sub="这些信息只显示在你自己的会议纪要 / 共享会议室里。" />
        <Card title="基本信息">
          <div className="meta" style={{ fontSize: 13 }}>正在加载用户信息…</div>
        </Card>
      </>
    );
  }

  return (
    <>
      <SecHeader tag="ACCOUNT" title="个人资料"
        sub="这些信息只显示在你自己的会议纪要 / 共享会议室里。" />

      {comingSoon && <SoonBanner message="个人资料保存功能即将上线 · 当前显示的是已登录账户信息" />}
      {error && <ErrorBanner message={error} />}
      {savedOk && (
        <div style={{
          padding: '10px 14px', marginBottom: 16,
          border: '1px solid var(--live)', borderRadius: 4,
          background: 'rgba(0,200,150,0.06)', color: 'var(--live)', fontSize: 12,
        }}>
          <span className="mono tiny" style={{ marginRight: 6, letterSpacing: '0.1em' }}>
            [OK]
          </span>
          已保存
        </div>
      )}

      <Card title="基本信息">
        <div className="flex gap-4" style={{ alignItems: 'center', marginBottom: 18 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--live)', color: 'var(--bg-0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--mono)', fontSize: 22, fontWeight: 600,
          }}>{initials}</div>
          <div className="col gap-1">
            <button className="btn ghost mono tiny" disabled>change avatar <SoonTag /></button>
            <span className="meta" style={{ fontSize: 11 }}>PNG / SVG · max 1MB</span>
          </div>
        </div>
        <Row label="显示名称">
          <input className="input" value={name} onChange={e => setName(e.target.value)}
            style={{ width: 220 }} placeholder="你的名字" />
        </Row>
        <Row label="邮箱" hint="用于登录和会议提醒">
          <div className="flex gap-2">
            <input className="input" value={email} onChange={e => setEmail(e.target.value)}
              style={{ width: 220 }} />
            {user.email_verified ? (
              <span className="tag mono tiny" style={{
                borderColor: 'var(--live)', color: 'var(--live)',
              }}>verified</span>
            ) : (
              <span className="tag mono tiny" style={{
                borderColor: 'var(--line-3)', color: 'var(--fg-2)',
              }}>unverified</span>
            )}
          </div>
        </Row>
        <Row label="账户余额" hint="可用于托管模式 LLM 调用">
          <span className="mono" style={{ fontSize: 13 }}>
            ${(user.credits_cents / 100).toFixed(2)}
          </span>
        </Row>
        <Row label="套餐">
          <span className="tag mono tiny" style={{
            borderColor: 'var(--live)', color: 'var(--live)',
          }}>{user.plan}</span>
        </Row>
        <Row label="时区" hint="影响会议提醒和纪要时间戳 (本地存储)">
          <select className="input" value={tz} onChange={e => setTz(e.target.value)}
            style={{ width: 220 }}>
            <option>Asia/Shanghai (UTC+8)</option>
            <option>UTC</option>
            <option>America/New_York (UTC−5)</option>
            <option>Europe/London (UTC+0)</option>
          </select>
        </Row>
        <Row label="语言 (UI)" hint="agent 默认输出语言会跟随 UI 语言 (本地存储)">
          <select className="input" defaultValue="中文 (简体)" style={{ width: 220 }}>
            <option>中文 (简体)</option>
            <option>English</option>
            <option>日本語</option>
          </select>
        </Row>
      </Card>

      <div className="flex" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn ghost mono" onClick={onCancel} disabled={saving}>cancel</button>
        <button className="btn primary mono" onClick={onSave} disabled={saving}>
          {saving ? '$ saving…' : '$ save changes'}
        </button>
      </div>
    </>
  );
}

function SecPrefs() {
  const [tweaks, setTweaks] = React.useState({
    autoStart: false,
    showTokenCost: true,
    pauseOnPunchline: true,
    streamTyping: true,
    soundOnFlag: true,
    defaultRoom: 'A',
    minutesStyle: 'concise',
  });
  const set = (k, v) => setTweaks(t => ({ ...t, [k]: v }));

  return (
    <>
      <SecHeader tag="PREFERENCES" title="使用偏好"
        sub="调你的会议室手感 — 跟会议本身的 agenda 配置无关。" />

      <SoonBanner message="偏好设置目前仅本地生效 · 后端持久化即将上线" />

      <Card title="会议运行" sub="agent 在场时的默认行为">
        <Row label="开 agenda 后自动开会"
          hint="默认 OFF · 让你有时间检查议程后再 ▷ start">
          <Toggle on={tweaks.autoStart} onChange={v => set('autoStart', v)} />
        </Row>
        <Row label="实时显示 token 计费"
          hint="状态条上挂着 $ 数字, 一边吵一边看烧了多少钱">
          <Toggle on={tweaks.showTokenCost} onChange={v => set('showTokenCost', v)} />
        </Row>
        <Row label="听到 punchline 时自动暂停"
          hint="当某个 agent 抛出关键洞察, 系统自动 pause 让你消化">
          <Toggle on={tweaks.pauseOnPunchline} onChange={v => set('pauseOnPunchline', v)} />
        </Row>
        <Row label="agent 输出 streaming 打字动画"
          hint="关掉会更快, 但少一点临场感">
          <Toggle on={tweaks.streamTyping} onChange={v => set('streamTyping', v)} />
        </Row>
        <Row label="举手 / 反对时播放音效"
          hint="非常轻的一声, 可关">
          <Toggle on={tweaks.soundOnFlag} onChange={v => set('soundOnFlag', v)} />
        </Row>
      </Card>

      <Card title="界面" sub="你的默认会议室隐喻">
        <Row label="默认 Room 风格" hint="Studio 里随时切换, 只是默认值">
          <div style={{
            display: 'inline-flex', padding: 3,
            border: '1px solid var(--line-2)', borderRadius: 4,
            background: 'var(--bg-2)',
          }}>
            {[['A', '终端'], ['B', '辩论台'], ['C', '议会']].map(([id, lbl]) => (
              <button key={id} onClick={() => set('defaultRoom', id)}
                style={{
                  padding: '6px 12px', borderRadius: 3, border: 'none',
                  background: tweaks.defaultRoom === id ? 'var(--bg-3)' : 'transparent',
                  color: tweaks.defaultRoom === id ? 'var(--fg-0)' : 'var(--fg-2)',
                  cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 11,
                }}>
                {id} · {lbl}
              </button>
            ))}
          </div>
        </Row>
        <Row label="主题" hint="目前只有暗色一种, 我们觉得 light mode 不太适合开会被拷问">
          <select className="input" defaultValue="dark" style={{ width: 180 }} disabled>
            <option>dark</option>
            <option>light (coming soon)</option>
          </select>
        </Row>
        <Row label="字号" hint="影响整个会议室文本">
          <select className="input" defaultValue="default" style={{ width: 180 }}>
            <option>compact</option>
            <option>default</option>
            <option>large</option>
          </select>
        </Row>
      </Card>

      <Card title="纪要 & 导出" sub="会议结束后自动生成的内容偏好">
        <Row label="纪要文风" hint="你的会议纪要怎么写">
          <select className="input" value={tweaks.minutesStyle}
            onChange={e => set('minutesStyle', e.target.value)} style={{ width: 200 }}>
            <option value="concise">concise · 一页 5 个 bullet</option>
            <option value="detailed">detailed · 完整论证链</option>
            <option value="academic">academic · 引用式</option>
            <option value="newsletter">newsletter · 给团队发的</option>
          </select>
        </Row>
        <Row label="自动归档到 Notion / Linear"
          hint="会议结束后自动推过去 · 需要先在 Workspace 配集成">
          <Toggle on={false} onChange={() => {}} />
        </Row>
      </Card>
    </>
  );
}

const PROVIDER_OPTIONS: { id: string; label: string }[] = [
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'openai',    label: 'OpenAI' },
  { id: 'google',    label: 'Google' },
  { id: 'deepseek',  label: 'DeepSeek' },
];

function SecLLM() {
  const [mode, setMode] = React.useState('managed');
  const [byokAvailable, setByokAvailable] = React.useState<boolean | null>(null);
  const [keys, setKeys] = React.useState<any[]>([]);
  const [loadingKeys, setLoadingKeys] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // add-key form
  const [newProvider, setNewProvider] = React.useState(PROVIDER_OPTIONS[0].id);
  const [newKey, setNewKey] = React.useState('');
  const [newLabel, setNewLabel] = React.useState('');
  const [adding, setAdding] = React.useState(false);

  const loadKeys = React.useCallback(async () => {
    setLoadingKeys(true);
    setError(null);
    try {
      const res = await me.listApiKeys();
      setKeys(res.keys || []);
      setByokAvailable(true);
    } catch (err) {
      if (err instanceof HTTPError && err.status === 404) {
        setByokAvailable(false);
      } else if (err instanceof HTTPError && err.status === 401) {
        setError('请先登录');
        setByokAvailable(false);
      } else {
        setError('加载 API key 列表失败');
        setByokAvailable(false);
      }
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  React.useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const onAddKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setAdding(true);
    setError(null);
    try {
      await me.addApiKey({
        provider: newProvider,
        key: newKey.trim(),
        label: newLabel.trim() || undefined,
      });
      setNewKey('');
      setNewLabel('');
      await loadKeys();
    } catch (err) {
      if (err instanceof HTTPError && err.status === 404) {
        setByokAvailable(false);
      } else if (err instanceof HTTPError) {
        setError(`添加失败 (HTTP ${err.status})`);
      } else {
        setError('添加失败 · 请检查网络');
      }
    } finally {
      setAdding(false);
    }
  };

  const onDelete = async (id: string) => {
    setError(null);
    try {
      await me.deleteApiKey(id);
      await loadKeys();
    } catch (err) {
      if (err instanceof HTTPError && err.status === 404) {
        setError('该 key 已不存在');
        await loadKeys();
      } else {
        setError('删除失败');
      }
    }
  };

  return (
    <>
      <SecHeader tag="LLM & API" title="模型与计费来源"
        sub="决定的是钱怎么走 · 跟 agent 能不能吵架无关。" />

      <Card title="计费模式">
        <div className="col gap-2">
          {[
            { id: 'managed', t: '托管 (默认)', d: '我们替你调 LLM, 按公开费率扣余额 · 不加 markup' },
            { id: 'byok',    t: 'BYOK 自带 key', d: '你自己接 LLM, 我们只收 $5/月 平台费' },
            { id: 'local',   t: '本地 (Llama-4)', d: '完全不出你机器, 免费 · 仅限 Hobby' },
          ].map(o => {
            const on = mode === o.id;
            return (
              <div key={o.id} onClick={() => setMode(o.id)} style={{
                padding: '12px 14px',
                border: '1px solid ' + (on ? 'var(--live)' : 'var(--line-2)'),
                background: on ? 'var(--bg-3)' : 'var(--bg-2)',
                borderRadius: 5, cursor: 'pointer',
                display: 'flex', gap: 12, alignItems: 'center',
              }}>
                <span style={{
                  width: 14, height: 14, borderRadius: '50%',
                  border: '1.5px solid ' + (on ? 'var(--live)' : 'var(--line-3)'),
                  background: on ? 'var(--live)' : 'transparent', flexShrink: 0,
                }} />
                <div className="grow">
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{o.t}</div>
                  <div className="meta" style={{ fontSize: 11 }}>{o.d}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {mode === 'byok' && (
        <Card title="API keys" sub="加密存储 (AES-256) · 我们看不到原文">
          {error && <ErrorBanner message={error} />}
          {byokAvailable === false && (
            <SoonBanner message="BYOK 功能即将上线 · 后端 endpoint 还未实现" />
          )}

          {byokAvailable && (
            <>
              {loadingKeys && (
                <div className="meta" style={{ fontSize: 12, padding: '8px 0' }}>
                  加载中…
                </div>
              )}

              {!loadingKeys && keys.length === 0 && (
                <div className="meta" style={{
                  fontSize: 12, padding: '14px 0', textAlign: 'center',
                  borderBottom: '1px solid var(--line-1)',
                }}>
                  还没添加任何 API key · 在下方添加
                </div>
              )}

              {!loadingKeys && keys.map((k: any) => {
                const provLabel = PROVIDER_OPTIONS.find(p => p.id === k.provider)?.label || k.provider;
                return (
                  <Row key={k.id} label={
                    <span>
                      <span style={{ fontWeight: 500 }}>{provLabel}</span>
                      {k.label && (
                        <span className="meta mono tiny" style={{ marginLeft: 8 }}>
                          · {k.label}
                        </span>
                      )}
                    </span>
                  } hint={k.masked || k.preview || k.key_preview || `id: ${k.id}`}>
                    <button className="btn ghost tiny mono"
                      onClick={() => onDelete(k.id)}
                      style={{ color: 'var(--err)' }}>
                      revoke
                    </button>
                  </Row>
                );
              })}

              <form onSubmit={onAddKey} style={{
                marginTop: 16, paddingTop: 16,
                borderTop: '1px solid var(--line-1)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
                  添加新 key
                </div>
                <div className="flex gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                  <select className="input mono"
                    value={newProvider}
                    onChange={e => setNewProvider(e.target.value)}
                    style={{ width: 130, fontSize: 12 }}>
                    {PROVIDER_OPTIONS.map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                  <input className="input mono"
                    type="password"
                    placeholder="sk-..."
                    value={newKey}
                    onChange={e => setNewKey(e.target.value)}
                    style={{ flex: '1 1 240px', fontSize: 11 }} />
                  <input className="input"
                    placeholder="label (可选)"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    style={{ width: 160, fontSize: 12 }} />
                  <button type="submit" className="btn primary tiny mono"
                    disabled={adding || !newKey.trim()}>
                    {adding ? 'adding…' : '+ add key'}
                  </button>
                </div>
              </form>
            </>
          )}
        </Card>
      )}

      <Card title="默认模型" sub="这些是新建 agent 默认用的 · 单 agent 可以单独覆盖">
        <Row label="主力推理模型" hint="多数 agent 用这个">
          <select className="input mono" defaultValue="claude-sonnet-4.5" style={{ width: 220 }}>
            <option>claude-sonnet-4.5</option>
            <option>gpt-4o</option>
            <option>gemini-2.5-pro</option>
            <option>llama-4-405b (本地)</option>
          </select>
        </Row>
        <Row label="快速模型" hint="评委 / 计时员等不需要慢思考的角色">
          <select className="input mono" defaultValue="claude-haiku-4.5" style={{ width: 220 }}>
            <option>claude-haiku-4.5</option>
            <option>gpt-4o-mini</option>
            <option>gemini-2.5-flash</option>
          </select>
        </Row>
        <Row label="温度 (创造性)"
          hint="低 = 收敛 / 严谨, 高 = 发散 / 想象">
          <input type="range" min="0" max="100" defaultValue="60"
            style={{ width: 180 }} />
        </Row>
      </Card>

      <Card title="预算护栏" sub="超了就不给跑, 别让 agent 自己玩疯了">
        <Row label="单场会议最大花费" hint="超过自动 ⏸ 暂停, 等你确认">
          <div className="flex gap-2 center">
            <span className="mono dim">$</span>
            <input className="input mono" type="number" defaultValue="2.00" step="0.5"
              style={{ width: 80 }} />
          </div>
        </Row>
        <Row label="月度总预算" hint="包含所有会议 · 触顶后所有新会议无法启动">
          <div className="flex gap-2 center">
            <span className="mono dim">$</span>
            <input className="input mono" type="number" defaultValue="50" step="10"
              style={{ width: 80 }} />
          </div>
        </Row>
      </Card>
    </>
  );
}

function SecNotifs() {
  const { user } = useAuth();
  return (
    <>
      <SecHeader tag="NOTIFICATIONS" title="通知"
        sub="什么样的事我们打扰你, 怎么打扰。" />

      <SoonBanner message="通知偏好持久化 · 后端 endpoint 即将上线" />

      <Card title="渠道">
        <Row label={`邮件 · ${user?.email || '未登录'}`} hint="重要会议节点和账单">
          <Toggle on={true} onChange={() => {}} />
        </Row>
        <Row label="浏览器 push" hint="agent 在后台说出关键洞察时叫你回来">
          <Toggle on={true} onChange={() => {}} />
        </Row>
        <Row label="Slack" hint="未连接 · 在 Workspace 里配集成">
          <button className="btn ghost mono tiny" disabled>connect <SoonTag /></button>
        </Row>
        <Row label="Webhook" hint="发给你自己的 endpoint · Pro 起">
          <button className="btn ghost mono tiny" disabled>+ add <SoonTag /></button>
        </Row>
      </Card>

      <Card title="什么时候提醒我">
        {[
          ['会议结束 · 纪要生成完毕', true],
          ['agent 主动举旗 (raise flag)', true],
          ['投票需要我做出 tie-breaker', true],
          ['月度预算用了 80%', true],
          ['月度预算用了 100%', true],
          ['每周用量摘要 (周一早上)', false],
          ['新功能上线', false],
          ['产品 newsletter (一月一封)', false],
        ].map(([t, def]) => (
          <Row key={t as string} label={t}>
            <Toggle on={def} onChange={() => {}} />
          </Row>
        ))}
      </Card>

      <Card title="勿扰时段" sub="这段时间所有 push / 邮件延后到结束">
        <Row label="勿扰" hint="当地时间">
          <Toggle on={true} onChange={() => {}} />
        </Row>
        <div className="flex gap-3 center" style={{ paddingTop: 10 }}>
          <input className="input mono" type="time" defaultValue="22:30" style={{ width: 110 }} />
          <span className="mono dim">→</span>
          <input className="input mono" type="time" defaultValue="08:00" style={{ width: 110 }} />
          <span className="meta">每天</span>
        </div>
      </Card>
    </>
  );
}

function SecWorkspace() {
  return (
    <>
      <SecHeader tag="WORKSPACE" title="协作空间"
        sub="升级到 Team 后, 这里管成员、共享 agent 库、集成。" />

      <SoonBanner message="Workspace / 集成功能即将上线" />

      <Card title="升级到 Team" sub="你目前的 Pro 套餐是单人空间">
        <div style={{
          padding: 24, border: '1px dashed var(--line-2)',
          borderRadius: 6, textAlign: 'center', background: 'var(--bg-2)',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>◫</div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
            想拉队友进来一起开会?
          </div>
          <div className="meta" style={{ fontSize: 12, marginBottom: 16, maxWidth: 360, margin: '0 auto 16px' }}>
            Team 套餐让你跟同事共享 agent 库 · 共享会议室 · SSO · 每人 2000 min/月。
          </div>
          <button className="btn-lg primary mono" disabled>$ upgrade to team → <SoonTag /></button>
        </div>
      </Card>

      <Card title="集成" sub="把会议纪要送出去 · 把上下文喂进来">
        {[
          ['◆', 'Notion', '把会议纪要自动归档到 Notion 数据库', false],
          ['◇', 'Linear', '会议结论自动建 issue · 指派给 owner', false],
          ['◓', 'Slack', '关键时刻 ping 团队频道', false],
          ['⌘', 'GitHub', '从 PR / issue 拉上下文进会议', false],
          ['⎘', 'Google Drive', '把附件读进 agent 上下文', false],
        ].map(([ic, name, d]) => (
          <Row key={name as string} label={
            <span><span className="mono dim" style={{ marginRight: 8 }}>{ic}</span>{name}</span>
          } hint={d}>
            <button className="btn ghost tiny mono" disabled>connect <SoonTag /></button>
          </Row>
        ))}
      </Card>
    </>
  );
}

function SecSecurity() {
  const { signOut } = useAuth();
  return (
    <>
      <SecHeader tag="SECURITY" title="安全"
        sub="登录、会话、双因素 · 别让别人替你开你的会。" />

      <SoonBanner message="密码 / 2FA / 会话管理后端即将上线 · 目前可登出" />

      <Card title="密码">
        <Row label="登录密码" hint="目前使用 magic link 登录">
          <button className="btn ghost mono tiny" disabled>change <SoonTag /></button>
        </Row>
      </Card>

      <Card title="双因素认证 (2FA)">
        <Row label="2FA 状态" hint="强烈推荐打开 · Team 起强制">
          <span className="tag mono tiny" style={{
            borderColor: 'var(--err)', color: 'var(--err)',
          }}>off</span>
        </Row>
        <Row label="Authenticator App" hint="Google / 1Password / Authy">
          <button className="btn primary tiny mono" disabled>enable <SoonTag /></button>
        </Row>
        <Row label="Passkey / WebAuthn" hint="用 Face ID / Touch ID / 硬件 key 替代密码">
          <button className="btn ghost tiny mono" disabled>+ add passkey <SoonTag /></button>
        </Row>
      </Card>

      <Card title="活跃会话" sub="任何看着不对的, 直接干掉">
        <div className="meta" style={{ fontSize: 12, padding: '14px 0' }}>
          会话列表即将上线 · 当前你可以直接登出本设备
        </div>
        <div style={{ paddingTop: 4 }}>
          <button className="btn ghost mono" onClick={() => signOut()}
            style={{ color: 'var(--err)' }}>
            $ sign out this device
          </button>
        </div>
      </Card>
    </>
  );
}

function SecData() {
  return (
    <>
      <SecHeader tag="DATA & EXPORT" title="数据 & 导出"
        sub="你的会议是你的。随时打包带走。" />

      <SoonBanner message="数据导出后端即将上线" />

      <Card title="导出">
        <Row label="导出全部会议 (JSON)"
          hint="所有会议、agent 配置、议程、纪要 · ZIP 包">
          <button className="btn ghost mono tiny" disabled>$ request export <SoonTag /></button>
        </Row>
        <Row label="按月导出 PDF 纪要" hint="用于审计 / 合规归档">
          <button className="btn ghost mono tiny" disabled>configure <SoonTag /></button>
        </Row>
      </Card>

      <Card title="数据保留" sub="过期会议自动清掉, 给你留点干净">
        <Row label="会议 transcript 保留期">
          <select className="input mono" defaultValue="forever" style={{ width: 180 }}>
            <option value="30">30 天</option>
            <option value="90">90 天</option>
            <option value="365">1 年</option>
            <option value="forever">永久 (默认)</option>
          </select>
        </Row>
        <Row label="允许我们用你的 transcript 改进模型" hint="默认 OFF · 我们尊重你">
          <Toggle on={false} onChange={() => {}} />
        </Row>
      </Card>
    </>
  );
}

function SecDanger({ goTo, handleSignOut }: { goTo: (r: string) => void; handleSignOut: () => void }) {
  const [confirm, setConfirm] = React.useState('');
  return (
    <>
      <SecHeader tag="DANGER ZONE" title="危险区"
        sub="这些操作不可逆。三思。" />

      <SoonBanner message="清空历史 / 删除账户 后端 endpoint 还未上线 · 可使用登出退出" />

      <div style={{
        border: '1px solid var(--err)', borderRadius: 6,
        background: 'rgba(239,76,76,0.04)', padding: 18, marginBottom: 16,
      }}>
        <div className="flex between center" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--err)' }}>
              登出
            </div>
            <div className="meta" style={{ fontSize: 12, marginTop: 2 }}>
              退出当前设备 · 不会删除任何数据
            </div>
          </div>
          <button className="btn ghost mono tiny" onClick={handleSignOut} style={{
            color: 'var(--err)', borderColor: 'var(--err)',
          }}>$ sign out</button>
        </div>
      </div>

      <div style={{
        border: '1px solid var(--err)', borderRadius: 6,
        background: 'rgba(239,76,76,0.04)', padding: 18, marginBottom: 16,
      }}>
        <div className="flex between center" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--err)' }}>
              清空所有会议历史
            </div>
            <div className="meta" style={{ fontSize: 12, marginTop: 2 }}>
              删掉你所有的 transcript / agenda / 纪要 · agent 库会保留
            </div>
          </div>
          <button className="btn ghost mono tiny" disabled style={{
            color: 'var(--err)', borderColor: 'var(--err)',
          }}>$ wipe history <SoonTag /></button>
        </div>
      </div>

      <div style={{
        border: '1px solid var(--err)', borderRadius: 6,
        background: 'rgba(239,76,76,0.04)', padding: 18,
      }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--err)', marginBottom: 4 }}>
          删除账户
        </div>
        <div className="meta" style={{ fontSize: 12, marginBottom: 14 }}>
          所有数据立即从我们的生产库删除, 30 天内从备份中清除。
          <br />正在进行的订阅会立即停止 (但不退款)。
          <br /><span className="mono tiny" style={{ color: 'var(--fg-2)' }}>[soon] 后端尚未实现</span>
        </div>
        <div className="col gap-2">
          <input className="input mono" placeholder='输入 "DELETE" 以确认'
            value={confirm} onChange={e => setConfirm(e.target.value)} />
          <button className="btn-lg mono"
            disabled={confirm !== 'DELETE'}
            onClick={() => { alert('demo · 后端未实现, 暂不支持'); goTo('landing'); }}
            style={{
              background: confirm === 'DELETE' ? 'var(--err)' : 'var(--bg-3)',
              color: confirm === 'DELETE' ? '#fff' : 'var(--fg-3)',
              border: '1px solid ' + (confirm === 'DELETE' ? 'var(--err)' : 'var(--line-2)'),
              cursor: confirm === 'DELETE' ? 'pointer' : 'not-allowed',
              alignSelf: 'flex-start',
            }}>
            $ delete account permanently
          </button>
        </div>
      </div>
    </>
  );
}
